/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import React, { type ReactNode } from 'react';
import Button from '@atlaskit/button/button';
import EmptyState from '@atlaskit/empty-state/empty-state';
import type { EmptyStateProps } from '@atlaskit/empty-state/types';
import { componentWithFG } from '@jira/platform__utils-feature-gates-gate-component/src/index.tsx';
// eslint-disable-next-line jira/restricted/@atlassian+jira-intl-v2
// eslint-disable-next-line jira/restricted/@atlassian+jira-intl-v2
// eslint-disable-next-line jira/restricted/@atlassian+jira-intl-v2
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import type {
	// eslint-disable-next-line jira/restricted/@atlassian+jira-intl-v2
	IntlShapeV2 as IntlShape,
	// eslint-disable-next-line jira/restricted/@atlassian+jira-intl-v2
	MessageDescriptorV2 as MessageDescriptor, // eslint-disable-next-line jira/restricted/@atlassian+jira-intl-v2
} from '@atlassian/jira-intl/src/v2/types.tsx';
import { defineMessage } from '@atlassian/jira-intl';
import errorSubtleImageUrl from './assets/error-subtle.svg';
import errorImageUrl from './assets/error.svg';

const retry = defineMessage({
	id: 'common.components.error-state.retry-action',
	defaultMessage: 'Try again',
	description: 'Text shown on a button that will retry a failed operation when clicked.',
});

// eslint-disable-next-line jira/export/restrict-types-re-export
export type Message = MessageDescriptor;

type Props = {
	intl: IntlShape;
	header?: string;
	description: ReactNode;
	size?: 'wide' | 'narrow';
	isLoading?: boolean;
	onRetry?: () => void;
	errorImage?: string;
	isSubtle?: boolean;
	imageWidth?: number;
	retryMessage?: MessageDescriptor;
	maxImageHeight?: number;
	renderErrorImage?: EmptyStateProps['renderImage'];
	headingLevel?: number;
};
function ErrorNew({
	header = '',
	description,
	size = 'wide' as const,
	onRetry,
	isLoading = false,
	intl: { formatMessage },
	errorImage,
	isSubtle = false,
	imageWidth,
	maxImageHeight,
	retryMessage = retry,
	renderErrorImage,
	headingLevel,
}: Props) {
	const buttonAppearance = isSubtle ? 'default' : 'primary';
	const defaultImageUrl = isSubtle ? errorSubtleImageUrl : errorImageUrl;

	const fallbackImage = errorImage || defaultImageUrl;

	let imageUrl;

	if (renderErrorImage) {
		imageUrl = undefined;
	} else {
		imageUrl = fallbackImage;
	}

	return (
		<EmptyState
			testId="common-components-error-state.empty-state"
			width={size}
			imageWidth={imageWidth}
			imageUrl={imageUrl}
			header={header}
			description={description}
			isLoading={isLoading}
			maxImageHeight={maxImageHeight}
			primaryAction={
				!onRetry ? null : (
					<Button appearance={buttonAppearance} isDisabled={isLoading} onClick={onRetry}>
						{formatMessage(retryMessage)}
					</Button>
				)
			}
			renderImage={renderErrorImage}
			headingLevel={headingLevel}
		/>
	);
}

function ErrorOld({
	header = '',
	description,
	size = 'wide' as const,
	onRetry,
	isLoading = false,
	intl,
	errorImage,
	isSubtle = false,
	imageWidth,
	maxImageHeight,
	retryMessage = retry,
	renderErrorImage,
	headingLevel,
}: Props) {
	const { formatMessage } = intl;
	const buttonAppearance = isSubtle ? 'default' : 'primary';
	const defaultImageUrl = isSubtle ? errorSubtleImageUrl : errorImageUrl;

	const fallbackImage = errorImage || defaultImageUrl;

	let imageUrl;

	if (renderErrorImage) {
		imageUrl = undefined;
	} else {
		imageUrl = fallbackImage;
	}

	return (
		<EmptyState
			testId="common-components-error-state.empty-state"
			width={size}
			imageWidth={imageWidth}
			imageUrl={imageUrl}
			header={header}
			description={description}
			isLoading={isLoading}
			maxImageHeight={maxImageHeight}
			primaryAction={
				!onRetry ? null : (
					<Button appearance={buttonAppearance} isDisabled={isLoading} onClick={onRetry}>
						{formatMessage(retryMessage)}
					</Button>
				)
			}
			renderImage={renderErrorImage}
			headingLevel={headingLevel}
		/>
	);
}

/**
 * Renders an error state component with customizable options such as header, description,
 * and an optional retry action. It supports loading states and can display a default or
 * custom error image. This component is intended for use in error handling scenarios
 * where a clear message and potential recovery action need to be presented to the user.
 */
export const Error: React.FC<Props> = componentWithFG(
	'jiv-migrate-default-props-error-state',
	ErrorNew,
	ErrorOld,
);

const ExportedComponent: React.FC<
	Pick<
		Omit<Props, 'intl'>,
		| 'header'
		| 'description'
		| 'size'
		| 'onRetry'
		| 'isLoading'
		| 'errorImage'
		| 'isSubtle'
		| 'imageWidth'
		| 'maxImageHeight'
		| 'retryMessage'
		| 'renderErrorImage'
		| 'headingLevel'
	> & {} & {} & {
		// oxlint-disable-next-line @typescript-eslint/no-explicit-any
		forwardedRef?: React.Ref<any>;
	}
> & {
	defaultProps?: undefined;
} = injectIntl(Error);
export default ExportedComponent;
